import React, { useState } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Dna, Lightning, Target, ArrowDown, ArrowUp, Copy, Info } from '@phosphor-icons/react';

const CrisprDesignerPage = () => {
  const [sequence, setSequence] = useState('');
  const [pam, setPam] = useState('NGG');
  const [guideLength, setGuideLength] = useState(20);
  const [organism, setOrganism] = useState('human');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState('combined_score');
  const [sortDir, setSortDir] = useState('desc');

  const design = async () => {
    if (!sequence.trim()) { toast.error('Enter a target sequence'); return; }
    if (sequence.replace(/[ATCGatcg\s\n]/g, '').length > 0) { toast.error('Invalid characters in sequence (use A, T, C, G only)'); return; }
    setLoading(true);
    try {
      const res = await api.designGrna(sequence.replace(/\s|\n/g, ''), pam, guideLength, organism);
      setResults(res);
      if (res.total_guides === 0) toast.info('No guide RNAs found for this sequence/PAM');
    } catch (e) { toast.error('Design failed'); }
    setLoading(false);
  };

  const sorted = results?.guides ? [...results.guides].sort((a, b) => {
    const mult = sortDir === 'desc' ? -1 : 1;
    return (a[sortBy] - b[sortBy]) * mult;
  }) : [];

  const copySeq = (s) => { navigator.clipboard.writeText(s); toast.success('Copied'); };

  const ScoreBar = ({ value, color }) => (
    <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
      <div className="h-full rounded-full transition-all" style={{ width: `${value}%`, backgroundColor: color }} />
    </div>
  );

  return (
    <div data-testid="crispr-designer-page">
      <div className="mb-6">
        <h1 className="heading-display flex items-center gap-2"><Dna size={24} className="text-violet-500" /> CRISPR gRNA Designer</h1>
        <p className="text-slate-400 mt-1">Design guide RNAs with off-target scoring and efficiency predictions</p>
      </div>

      {/* Input Panel */}
      <div className="card-flat p-5 mb-6" data-testid="crispr-input">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-4">
          <div className="lg:col-span-4">
            <label className="text-xs font-medium text-slate-400 mb-1 block">Target DNA Sequence (5'→3')</label>
            <textarea
              className="input-field w-full font-mono text-sm"
              rows={4}
              placeholder="ATCGATCGATCGATCGATCG..."
              value={sequence}
              onChange={e => setSequence(e.target.value.toUpperCase())}
              data-testid="sequence-input"
            />
            <p className="text-[10px] text-slate-400 mt-1">{sequence.replace(/\s|\n/g, '').length} bp</p>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-400 mb-1 block">PAM Sequence</label>
            <select className="input-field w-full" value={pam} onChange={e => setPam(e.target.value)} data-testid="pam-select">
              <option value="NGG">NGG (SpCas9)</option>
              <option value="NAG">NAG (SpCas9)</option>
              <option value="NNGRRT">NNGRRT (SaCas9)</option>
              <option value="TTTV">TTTV (Cas12a/Cpf1)</option>
              <option value="NGN">NGN (SpG)</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-400 mb-1 block">Guide Length</label>
            <select className="input-field w-full" value={guideLength} onChange={e => setGuideLength(Number(e.target.value))}>
              <option value={17}>17 nt</option>
              <option value={18}>18 nt</option>
              <option value={19}>19 nt</option>
              <option value={20}>20 nt</option>
              <option value={21}>21 nt</option>
              <option value={23}>23 nt</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-400 mb-1 block">Target Organism</label>
            <select className="input-field w-full" value={organism} onChange={e => setOrganism(e.target.value)}>
              <option value="human">Human (H. sapiens)</option>
              <option value="mouse">Mouse (M. musculus)</option>
              <option value="ecoli">E. coli</option>
              <option value="yeast">Yeast (S. cerevisiae)</option>
              <option value="zebrafish">Zebrafish (D. rerio)</option>
              <option value="drosophila">Drosophila</option>
            </select>
          </div>
          <div className="flex items-end">
            <Button className="btn-primary w-full flex items-center justify-center gap-2" onClick={design} disabled={loading} data-testid="design-btn">
              {loading ? <div className="spinner-sm" /> : <><Target size={16} /> Design gRNAs</>}
            </Button>
          </div>
        </div>
      </div>

      {/* Results */}
      {results && (
        <div data-testid="crispr-results">
          <div className="flex items-center justify-between mb-4">
            <h2 className="heading-section">{results.total_guides} Guide RNAs Found</h2>
            <div className="flex items-center gap-2 text-xs text-slate-400">
              <span>Sort by:</span>
              {['combined_score', 'off_target_score', 'efficiency_score', 'gc_content'].map(key => (
                <button
                  key={key}
                  className={`px-2 py-1 rounded ${sortBy === key ? 'bg-indigo-900/40 text-indigo-300' : 'hover:bg-slate-100'}`}
                  onClick={() => { if (sortBy === key) setSortDir(d => d === 'desc' ? 'asc' : 'desc'); else { setSortBy(key); setSortDir('desc'); } }}
                >
                  {key.replace('_', ' ').replace('score', '').trim()}
                  {sortBy === key && (sortDir === 'desc' ? <ArrowDown size={10} className="inline ml-0.5" /> : <ArrowUp size={10} className="inline ml-0.5" />)}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            {sorted.map((g, i) => (
              <div key={i} className="card-flat p-4 hover:border-slate-700 transition-colors" data-testid={`grna-${i}`}>
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold" style={{ backgroundColor: g.combined_score >= 70 ? '#dcfce7' : g.combined_score >= 50 ? '#fef3c7' : '#fee2e2', color: g.combined_score >= 70 ? '#166534' : g.combined_score >= 50 ? '#92400e' : '#991b1b' }}>
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <code className="text-sm font-mono font-bold tracking-wide" style={{ color: '#F1F5F9' }}>{g.sequence}</code>
                      <span className="text-[10px] px-1.5 py-0.5 bg-slate-100 rounded">{g.pam_site}</span>
                      <span className="text-[10px] text-slate-400">pos {g.position} ({g.strand})</span>
                      <button onClick={() => copySeq(g.sequence)} className="text-slate-400 hover:text-teal-500"><Copy size={12} /></button>
                    </div>
                    <div className="grid grid-cols-4 gap-3 mt-2">
                      <div>
                        <p className="text-[10px] text-slate-400 mb-0.5">Combined</p>
                        <p className="text-sm font-bold" style={{ color: g.combined_score >= 70 ? '#166534' : g.combined_score >= 50 ? '#92400e' : '#991b1b' }}>{g.combined_score}</p>
                        <ScoreBar value={g.combined_score} color={g.combined_score >= 70 ? '#22c55e' : g.combined_score >= 50 ? '#eab308' : '#ef4444'} />
                      </div>
                      <div>
                        <p className="text-[10px] text-slate-400 mb-0.5">Off-target</p>
                        <p className="text-sm font-bold text-indigo-400">{g.off_target_score}</p>
                        <ScoreBar value={g.off_target_score} color="#3b82f6" />
                      </div>
                      <div>
                        <p className="text-[10px] text-slate-400 mb-0.5">Efficiency</p>
                        <p className="text-sm font-bold text-violet-600">{g.efficiency_score}</p>
                        <ScoreBar value={g.efficiency_score} color="#8b5cf6" />
                      </div>
                      <div>
                        <p className="text-[10px] text-slate-400 mb-0.5">GC Content</p>
                        <p className="text-sm font-bold text-indigo-400">{g.gc_content}%</p>
                        <ScoreBar value={g.gc_content} color={g.gc_content >= 40 && g.gc_content <= 70 ? '#0d9488' : '#f97316'} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CrisprDesignerPage;
